import {
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysDeliveryNote } from '../queryKeys';

import { fetchDeliveryNoteSimpleFormat } from './fetchDeliveryNoteSimpleFormat';

/**
 * Generates the query options for fetching the simplified JSON export of a
 * single delivery note.
 *
 * @param deliveryNoteId - The delivery note ID to export.
 * @param options - Additional options for the query.
 * @returns The query options including query key, query function, and cache settings.
 */
export const getDeliveryNoteSimpleFormatQueryOptions = <TData = Blob>({
  deliveryNoteId,
  options,
}: {
  deliveryNoteId: UUID | undefined;
  options?: Omit<UseQueryOptions<Blob, Error, TData>, 'queryKey' | 'queryFn'>;
}) =>
  queryOptions({
    gcTime: ms('10m'),
    queryFn: deliveryNoteId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchDeliveryNoteSimpleFormat(deliveryNoteId, { signal })
      : skipToken,
    queryKey: queryKeysDeliveryNote.getSimpleFormat(deliveryNoteId ?? ''),
    staleTime: ms('60s'),
    ...options,
  });

/**
 * React Query hook for fetching the simplified JSON export of a single
 * delivery note.
 *
 * @param deliveryNoteId - The delivery note ID to export.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/get_delivery_note_by_id_asset_delivery_notes__dln_id__get
 */
export const useQueryDeliveryNoteSimpleFormat = (
  deliveryNoteId: UUID | undefined,
  options?: Omit<UseQueryOptions<Blob, Error, Blob>, 'queryKey' | 'queryFn'>,
) =>
  useQuery(
    getDeliveryNoteSimpleFormatQueryOptions<Blob>({
      deliveryNoteId,
      options,
    }),
  );
